import type { Locale } from "antd/es/locale";

/**
 * Ant Design ichki matnlari uchun o'zbekcha (lotin) locale — P7 spec §7.
 *
 * ⚠️ Ant Design'ning o'zida ham `uz_UZ` nomli tayyor locale bor
 * (`antd/locale/uz_UZ`), lekin unda sifat nuqsoni bor: `antd@6.6.1`da
 * `Form.defaultValidateMessages.string.min` ichida RUS tilidagi matn
 * qolib ketgan ("... должна быть больше или равна ${min} символов").
 * Shu sababli bu fayl NOLDAN, `en_US` strukturasi asosida
 * (`antd/es/locale/en_US.ts`) yozilgan — kalitlar to'plami bir xil,
 * lekin har bir qiymat shu loyiha uchun qo'lda yozilgan/tekshirilgan.
 *
 * `DatePicker` va `Calendar` bitta `datePickerLocale` obyektini
 * bo'lishadi — Ant Design'ning o'z locale fayllari ham shunday qiladi
 * (`calendar/locale/*.ts` `date-picker/locale/*.ts` ni qayta eksport
 * qiladi), chunki taqvim komponenti sana tanlagich bilan bir xil
 * matnlardan foydalanadi.
 */
const typeTemplate = "${label} — ${type} turiga mos emas";

const timePickerLocale = {
	placeholder: "Vaqtni tanlang",
	rangePlaceholder: ["Boshlanish vaqti", "Tugash vaqti"] as [string, string],
};

const datePickerLocale = {
	lang: {
		locale: "uz_UZ",
		placeholder: "Sanani tanlang",
		yearPlaceholder: "Yilni tanlang",
		quarterPlaceholder: "Chorakni tanlang",
		monthPlaceholder: "Oyni tanlang",
		weekPlaceholder: "Haftani tanlang",
		rangePlaceholder: ["Boshlanish sanasi", "Tugash sanasi"] as [string, string],
		rangeYearPlaceholder: ["Boshlanish yili", "Tugash yili"] as [string, string],
		rangeMonthPlaceholder: ["Boshlanish oyi", "Tugash oyi"] as [string, string],
		rangeWeekPlaceholder: ["Boshlanish haftasi", "Tugash haftasi"] as [string, string],
		today: "Bugun",
		now: "Hozir",
		backToToday: "Bugungi kunga qaytish",
		ok: "OK",
		clear: "Tozalash",
		week: "Hafta",
		month: "Oy",
		year: "Yil",
		timeSelect: "vaqtni tanlash",
		dateSelect: "sanani tanlash",
		weekSelect: "Haftani tanlang",
		monthSelect: "Oyni tanlang",
		yearSelect: "Yilni tanlang",
		decadeSelect: "O'n yillikni tanlang",
		previousMonth: "Oldingi oy",
		nextMonth: "Keyingi oy",
		previousYear: "Oldingi yil",
		nextYear: "Keyingi yil",
		previousDecade: "Oldingi o'n yillik",
		nextDecade: "Keyingi o'n yillik",
		previousCentury: "Oldingi asr",
		nextCentury: "Keyingi asr",
		// ⚠️ `shortWeekDays`/`shortMonths` YOZILMASA taqvim sarlavhasi
		// `generateConfig.locale.getShortWeekDays("uz_UZ")` ga tushadi —
		// bu esa dayjs'ning RO'YXATDAN O'TKAZILGAN `uz` locale'iga tayanadi
		// (`@rc-component/picker/es/PickerPanel/DatePanel/index.js`).
		// Loyihada dayjs uz locale'i HECH QAYERDA yuklanmagan
		// (bu ataylab keyingi bosqichga qoldirilgan), ya'ni fallback
		// dayjs standartiga (INGLIZCHA "Su Mo Tu…", "Jan Feb…") tushib
		// qolardi. Shu ikki massiv aynan shu fallback'ni butunlay chetlab
		// o'tadi — Ant Design'ning o'z `uz_UZ` locale fayli ham (rc-picker
		// darajasida) bu ikkisini bermaydi, ya'ni bu YANGI tuzatish.
		shortWeekDays: ["Ya", "Du", "Se", "Ch", "Pa", "Ju", "Sh"],
		shortMonths: [
			"Yan",
			"Fev",
			"Mar",
			"Apr",
			"May",
			"Iyun",
			"Iyul",
			"Avg",
			"Sen",
			"Okt",
			"Noy",
			"Dek",
		],
		yearFormat: "YYYY",
		dayFormat: "D",
		cellMeridiemFormat: "A",
		monthBeforeYear: true,
	},
	timePickerLocale,
};

export const antdUz: Locale = {
	locale: "uz",
	global: {
		placeholder: "Iltimos, tanlang",
		close: "Yopish",
		sortable: "saralanadigan",
		show: "Ko'rsatish",
		hide: "Yashirish",
	},
	Pagination: {
		items_per_page: "/ sahifa",
		jump_to: "O'tish",
		jump_to_confirm: "tasdiqlash",
		page: "Sahifa",
		prev_page: "Oldingi sahifa",
		next_page: "Keyingi sahifa",
		prev_5: "Oldingi 5",
		next_5: "Keyingi 5",
		prev_3: "Oldingi 3",
		next_3: "Keyingi 3",
		page_size: "sahifa hajmi",
	},
	DatePicker: datePickerLocale,
	TimePicker: timePickerLocale,
	Calendar: datePickerLocale,
	Table: {
		filterTitle: "Filtr menyusi",
		filterConfirm: "OK",
		filterReset: "Tozalash",
		filterEmptyText: "Filtrlar yo'q",
		filterCheckAll: "Barcha elementlarni tanlash",
		filterSearchPlaceholder: "Filtrlardan qidirish",
		emptyText: "Ma'lumot yo'q",
		selectAll: "Joriy sahifani tanlash",
		selectInvert: "Joriy sahifa tanlovini teskarilash",
		selectNone: "Barcha ma'lumotni tozalash",
		selectionAll: "Barcha ma'lumotni tanlash",
		sortTitle: "Saralash",
		expand: "Qatorni yoyish",
		collapse: "Qatorni yig'ish",
		triggerDesc: "Kamayish tartibida saralash uchun bosing",
		triggerAsc: "O'sish tartibida saralash uchun bosing",
		cancelSort: "Saralashni bekor qilish uchun bosing",
	},
	Tour: {
		Next: "Keyingisi",
		Previous: "Oldingisi",
		Finish: "Tugatish",
	},
	Modal: {
		okText: "OK",
		cancelText: "Bekor qilish",
		justOkText: "OK",
	},
	Popconfirm: {
		okText: "OK",
		cancelText: "Bekor qilish",
	},
	Transfer: {
		titles: ["", ""],
		searchPlaceholder: "Qidiruv",
		itemUnit: "ta element",
		itemsUnit: "ta element",
		remove: "O'chirish",
		selectCurrent: "Joriy sahifani tanlash",
		removeCurrent: "Joriy sahifani o'chirish",
		selectAll: "Barchasini tanlash",
		deselectAll: "Tanlovni bekor qilish",
		removeAll: "Barchasini o'chirish",
		selectInvert: "Tanlovni teskarilash",
	},
	Upload: {
		uploading: "Yuklanmoqda...",
		removeFile: "Faylni o'chirish",
		uploadError: "Yuklashda xatolik yuz berdi",
		previewFile: "Faylni ko'rish",
		downloadFile: "Faylni yuklab olish",
	},
	Empty: {
		description: "Ma'lumot yo'q",
	},
	Icon: {
		icon: "ikonka",
	},
	Text: {
		edit: "Tahrirlash",
		copy: "Nusxalash",
		copied: "Nusxalandi",
		expand: "Yoyish",
		collapse: "Yig'ish",
	},
	Carousel: {
		prevSlide: "Oldingi slayd",
		nextSlide: "Keyingi slayd",
	},
	Form: {
		optional: "(ixtiyoriy)",
		defaultValidateMessages: {
			default: "${label} maydonini tekshirishda xatolik yuz berdi",
			required: "Iltimos, ${label} ni kiriting",
			enum: "${label} quyidagilardan biri bo'lishi kerak: [${enum}]",
			whitespace: "${label} bo'sh bo'lishi mumkin emas",
			date: {
				format: "${label} sana formati noto'g'ri",
				parse: "${label} sanaga aylantirib bo'lmadi",
				invalid: "${label} — yaroqsiz sana",
			},
			types: {
				string: typeTemplate,
				method: typeTemplate,
				array: typeTemplate,
				object: typeTemplate,
				number: typeTemplate,
				date: typeTemplate,
				boolean: typeTemplate,
				integer: typeTemplate,
				float: typeTemplate,
				regexp: typeTemplate,
				email: typeTemplate,
				url: typeTemplate,
				hex: typeTemplate,
			},
			string: {
				len: "${label} aynan ${len} ta belgidan iborat bo'lishi kerak",
				min: "${label} kamida ${min} ta belgidan iborat bo'lishi kerak",
				max: "${label} ko'pi bilan ${max} ta belgidan iborat bo'lishi kerak",
				range: "${label} ${min}-${max} ta belgi orasida bo'lishi kerak",
			},
			number: {
				len: "${label} ${len} ga teng bo'lishi kerak",
				min: "${label} kamida ${min} bo'lishi kerak",
				max: "${label} ko'pi bilan ${max} bo'lishi kerak",
				range: "${label} ${min}-${max} oralig'ida bo'lishi kerak",
			},
			array: {
				len: "${label} soni ${len} ta bo'lishi kerak",
				min: "${label} soni kamida ${min} ta bo'lishi kerak",
				max: "${label} soni ko'pi bilan ${max} ta bo'lishi kerak",
				range: "${label} soni ${min}-${max} orasida bo'lishi kerak",
			},
			pattern: {
				mismatch: "${label} ${pattern} andazasiga mos kelmaydi",
			},
		},
	},
	QRCode: {
		expired: "QR-kod muddati tugagan",
		refresh: "Yangilash",
		scanned: "Skanerlandi",
	},
	ColorPicker: {
		presetEmpty: "Bo'sh",
		transparent: "Shaffof",
		singleColor: "Bitta rang",
		gradientColor: "Gradient",
	},
};

export default antdUz;
